import React, { type JSX } from 'react';
import Link from 'next/link';
import { useMediaQuery } from '@monofront/business/hooks';
import { useTranslations } from 'next-intl';

const Breadcrumb = ({ children, UNSAFE_className = '', ...props }) => {
  const isMobile = useMediaQuery(800);
  const tGlobal = useTranslations('Global');

  const flattenedChildren: JSX.Element[] = []
    .concat(...children)
    .filter((child) => child !== undefined);

  const lastDisplayedElementOnMobile =
    flattenedChildren.length -
    1 -
    [...flattenedChildren]
      .reverse()
      .findIndex((child) => !child.props.isHiddenOnMobile);

  return (
    <>
      <nav
        {...props}
        className={`${
          isMobile ? 'mobile-nav' : 'desktop-nav'
        } ${UNSAFE_className}`}
        aria-label={tGlobal('breadcrumb')}
      >
        <ol className="tw-inline tw-list-none">
          {flattenedChildren.map((child, index) => (
            <React.Fragment key={index}>
              <li
                className={`
              tw-inline
              after:tw-mx-3 after:tw-inline-block after:tw-h-[11px]
              after:tw-w-px after:tw-rotate-[14deg] after:tw-bg-black-600 after:tw-content-['']
              last:after:tw-hidden
              ${
                lastDisplayedElementOnMobile === index
                  ? 'after:tw-hidden desktop:after:tw-inline-block'
                  : ''
              }
              ${
                child.props.isHiddenOnMobile
                  ? 'tw-hidden desktop:tw-inline'
                  : ''
              }`}
              >
                {child}
              </li>
            </React.Fragment>
          ))}
        </ol>
      </nav>
      <style jsx>{`
        nav {
          display: flex;
          align-items: center;
        }

        .mobile-nav {
          overflow: hidden;
          white-space: nowrap;
        }

        @media (max-width: 800px) {
          nav :global(ol) {
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
          }
        }

        .desktop-nav {
          flex-wrap: wrap;
          font-size: var(--font-size-1-5);
        }

        .breadcrumb-separator {
          margin-left: var(--space-3);
          margin-right: var(--space-3);
        }
      `}</style>
    </>
  );
};

interface IBreadcrumbLinkProps {
  href?: string;
  children: React.ReactElement<any> | string;
  isCurrentPage?: boolean;
  isHiddenOnMobile?: boolean;
  UNSAFE_className?: string;
}

const BreadcrumbLink: React.FC<IBreadcrumbLinkProps> = ({
  children,
  href,
  isCurrentPage = false,
  UNSAFE_className = '',
}) =>
  href ? (
    <Link
      href={href}
      className={`tw-lf-link-sm ${UNSAFE_className}`}
      data-testid={`BreadcrumbLink-go-to-${href}`}
      aria-current={isCurrentPage ? 'page' : undefined}
    >
      {children}
    </Link>
  ) : (
    <span
      data-testid="BreadcrumbLink-go-to-current-page"
      className={UNSAFE_className}
      aria-current={isCurrentPage ? 'page' : undefined}
    >
      {children}
    </span>
  );

Breadcrumb.Link = BreadcrumbLink;

export default Breadcrumb;
